import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
	Box,
	Checkbox,
	Dialog,
	FormControlLabel,
	MenuItem,
	Menu,
	TextField,
	ToggleButton,
	ToggleButtonGroup,
	Typography,
	IconButton,
} from '@mui/material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import TableRowsIcon from '@mui/icons-material/TableRows'
import ViewHeadlineIcon from '@mui/icons-material/ViewHeadline'
import EditRoundedIcon from '@mui/icons-material/EditRounded'
import MoreVertRoundedIcon from '@mui/icons-material/MoreVertRounded'
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded'
import { type GridColDef } from '@mui/x-data-grid-pro'
import { useModal } from '../../../../../store/ModalProvider'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import dayjs, { Dayjs } from 'dayjs'
import CustomButton from '../../CustomButton/CustomButton'
import { AnimatedDataGridPro } from '../../CustomDataGrid'
import CustomColumnMenu, { columnMenuPopperSx } from '../../CustomDataGrid/CustomColumnMenu'
import { popupActionButtonSx, popupSecondaryButtonSx } from '../popupButtonStyles'
import { calendarInputSx, calendarPopperSlotProps, popupInputLabelSx } from '../popupInputStyles'
import type {
	DriverServiceTotalsByTypeRow,
	DriverServiceTotalsModalData,
	DriverServiceTotalsRow,
	VehicleServiceTotalsModalData,
} from '../../../../../types/modal'
import { useNavigate } from 'react-router-dom'

// Invisible but clickable opener so picker still opens when clicked
const EmptyButton: React.ComponentType<any> = (props: any) => (
	<IconButton
		{...props}
		sx={{ ...(props?.sx || {}), opacity: 0 }}
	/>
)

type DensityMode = 'compact' | 'standard'

const dialogShadow =
	'0px 9px 46px 8px rgba(9, 32, 86, 0.12), 0px 24px 38px 3px rgba(9, 32, 86, 0.14), 0px 11px 15px -7px rgba(9, 32, 86, 0.2)'

const inputSx = {
	'& .MuiInputLabel-root': {
		...popupInputLabelSx,
	},
	'& .MuiOutlinedInput-root': {
		backgroundColor: '#FFFFFF',
		borderRadius: '4px',
		minHeight: '40px',
		fontSize: '13px',
		fontWeight: 500,
		color: '#092056',
		'& fieldset': {
			borderColor: 'rgba(134, 149, 175, 0.3)',
		},
		'&:hover fieldset': {
			borderColor: 'rgba(0, 165, 255, 0.4)',
		},
		'&.Mui-focused fieldset': {
			borderColor: 'rgba(0, 165, 255, 0.6)',
		},
	},
	'& .MuiOutlinedInput-input': {
		padding: '8px 12px',
		lineHeight: 1.3,
	},
	'& .MuiSelect-select': {
		padding: '8px 36px 8px 12px',
		display: 'flex',
		alignItems: 'center',
	},
	'& .MuiSelect-icon': {
		color: '#00A5FF',
		right: '10px',
	},
}

const readonlyFieldSx = {
	'& .MuiInputLabel-root': {
		...popupInputLabelSx,
	},
	'& .MuiOutlinedInput-root': {
		backgroundColor: '#F5F7FC',
		borderRadius: '4px',
		minHeight: '40px',
		fontSize: '13px',
		fontWeight: 500,
		color: '#092056',
		'& fieldset': {
			borderColor: 'rgba(134, 149, 175, 0)',
		},
		'&:hover fieldset': {
			borderColor: 'rgba(134, 149, 175, 0)',
		},
		'&.Mui-focused fieldset': {
			borderColor: 'rgba(134, 149, 175, 0)',
		},
	},
	'& .MuiOutlinedInput-input': {
		padding: '8px 12px',
		lineHeight: 1.3,
		WebkitTextFillColor: '#092056',
	},
}

const toggleGroupSx = {
	border: '1px solid rgba(134, 149, 175, 0.15)',
	borderRadius: '4px',
	'& .MuiToggleButton-root': {
		border: 'none',
		borderRadius: 0,
		padding: '4.8px',
		'&:not(:last-child)': {
			borderRight: '0.6px solid rgba(134, 149, 175, 0.15)',
		},
		'&.Mui-selected': {
			backgroundColor: '#F5F7FC',
			'&:hover': {
				backgroundColor: '#F5F7FC',
			},
		},
	},
}

const tableSx = {
	border: 'none',
	'--DataGrid-rowBorderColor': 'transparent',
	'& .MuiDataGrid-columnHeaders': {
		backgroundColor: '#FFFFFF',
		borderBottom: '1px solid rgba(134, 149, 175, 0.15)',
		borderTop: 'none',
		boxShadow: 'none',
	},
	'& .MuiDataGrid-columnHeaderTitle': {
		fontFamily: 'Poppins, sans-serif',
		fontWeight: 600,
		fontSize: '12px',
		color: '#8695AF',
		letterSpacing: '0.17px',
		lineHeight: '24px',
		textTransform: 'none',
	},
	'& .MuiDataGrid-columnSeparator': {
		opacity: 0,
		color: 'rgba(134, 149, 175, 0.6)',
		transition: 'opacity 0.2s ease',
	},
	'& .MuiDataGrid-columnHeader:hover .MuiDataGrid-columnSeparator': {
		opacity: 1,
	},
	'& .MuiDataGrid-columnSeparator--resizable': {
		cursor: 'col-resize',
	},
	'& .MuiDataGrid-columnHeader': {
		borderRight: 'none',
	},
	'& .MuiDataGrid-cell': {
		fontFamily: 'Poppins, sans-serif',
		fontSize: '12px',
		color: '#092056',
		borderBottom: 'none',
		borderRight: 'none',
	},
	'& .MuiDataGrid-row': {
		borderBottom: 'none',
	},
	'& .MuiDataGrid-row:hover': {
		backgroundColor: 'rgba(0, 165, 255, 0.06)',
	},
	'& .MuiDataGrid-row.row--animate': {
		animation: 'rowFadeIn 240ms ease-out',
	},
	'& .row--alert': {
		backgroundColor: 'rgba(255, 232, 216, 0.6)',
	},
	'& .row--warning': {
		backgroundColor: 'rgba(255, 243, 226, 0.7)',
	},
	'@keyframes rowFadeIn': {
		from: { opacity: 0 },
		to: { opacity: 1 },
	},
	'& .MuiDataGrid-cell:focus, & .MuiDataGrid-columnHeader:focus': {
		outline: 'none',
	},
}

const totalsTableSx = {
	'& .MuiDataGrid-columnHeader': {
		padding: '0 16px',
	},
	'& .MuiDataGrid-cell': {
		padding: '0 16px',
		display: 'flex',
		alignItems: 'center',
	},
	'& .row--totals': {
		backgroundColor: 'rgba(245, 247, 252, 0.5)',
	},
}

const servicesTableSx = {
	'& .MuiDataGrid-columnHeader': {
		padding: '0 16px',
	},
	'& .MuiDataGrid-cell': {
		padding: '0 16px',
		display: 'flex',
		alignItems: 'center',
	},
	'& .serviceRowActions': {
		opacity: 0,
		pointerEvents: 'none',
		transition: 'opacity 0.15s ease',
	},
	'& .MuiDataGrid-row:hover .serviceRowActions': {
		opacity: 1,
		pointerEvents: 'auto',
	},
}

const menuPaperSx = {
	borderRadius: '4px',
	boxShadow:
		'0px 3px 14px 2px rgba(9, 32, 86, 0.12), 0px 8px 10px 1px rgba(9, 32, 86, 0.14), 0px 5px 5px -3px rgba(9, 32, 86, 0.2)',
}

const checkboxSx = {
	color: '#8695AF',
	'&.Mui-checked': {
		color: '#00A5FF',
	},
}

const checkboxLabelSx = {
	fontFamily: 'Poppins, sans-serif',
	fontSize: '12px',
	fontWeight: 500,
	color: '#092056',
}

// Create dynamic styles based on density for animated row transitions
const getDynamicRowStyles = (density: DensityMode) => {
	const rowHeight = density === 'compact' ? 32 : 40

	return {
		// Set CSS variables that override MUI's internal variables
		'--DataGrid-rowHeight': `${rowHeight}px !important`,
		'--density-row-height': `${rowHeight}px !important`,
		'--height': `${rowHeight}px !important`,

		// Animate both the individual rows AND the container
		'&.MuiDataGrid-root': {
			// Animate the main DataGrid container height
			transition: 'height 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important',

			'& .MuiDataGrid-main': {
				transition: 'height 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important',
			},

			'& .MuiDataGrid-virtualScroller': {
				transition: 'height 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important',
			},

			'& .MuiDataGrid-virtualScrollerContent': {
				transition: 'height 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important',
			},

			// Individual row animations (this was working!)
			'& .MuiDataGrid-row': {
				height: `${rowHeight}px !important`,
				minHeight: `${rowHeight}px !important`,
				maxHeight: `${rowHeight}px !important`,
				transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important',
				willChange: 'height',
			},

			'& .MuiDataGrid-cell': {
				height: `${rowHeight}px !important`,
				minHeight: `${rowHeight}px !important`,
				maxHeight: `${rowHeight}px !important`,
				lineHeight: `${rowHeight - 1}px !important`,
				transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important',
				display: 'flex !important',
				alignItems: 'center !important',
				willChange: 'height',
			},
		},
	}
}

const defaultServices: DriverServiceTotalsRow[] = [
	{
		id: '1',
		ponto: 'Sim',
		requisicao: '95509',
		viatura: '0153',
		dataInicio: '02/05/2024',
		horaInicio: '09:00',
		localInicio: 'Lisboa',
		localTermino: 'Torres Vedras',
		horaTermino: '10:00',
		dataTermino: '02/05/2024',
		cliente: 'Best Transfers 4 U',
		valor: '90,85€',
	},
	{
		id: '2',
		ponto: 'Sim',
		requisicao: '84211',
		viatura: '0194',
		dataInicio: '03/05/2024',
		horaInicio: '14:00',
		localInicio: 'Lisboa',
		localTermino: 'Almada',
		horaTermino: '15:00',
		dataTermino: '03/05/2024',
		cliente: 'Scenic Tours',
		valor: '90,00€',
	},
	{
		id: '3',
		ponto: 'Não',
		requisicao: '84212',
		viatura: '0195',
		dataInicio: '03/05/2024',
		horaInicio: '09:00',
		localInicio: 'Braga',
		localTermino: 'Guimarães',
		horaTermino: '10:30',
		dataTermino: '03/05/2024',
		cliente: 'Cultural Walks',
		valor: '60,00€',
	},
	{
		id: '4',
		ponto: 'Sim',
		requisicao: '84213',
		viatura: '0196',
		dataInicio: '04/05/2024',
		horaInicio: '16:00',
		localInicio: 'Coimbra',
		localTermino: 'Figueira da Foz',
		horaTermino: '17:30',
		dataTermino: '04/05/2024',
		cliente: 'Sunset Escapes',
		valor: '80,00€',
	},
	{
		id: '5',
		ponto: 'Sim',
		requisicao: '84214',
		viatura: '0197',
		dataInicio: '05/05/2024',
		horaInicio: '09:00',
		localInicio: 'Lisboa',
		localTermino: 'Porto',
		horaTermino: '12:30',
		dataTermino: '05/05/2024',
		cliente: 'Urban Adventures',
		valor: '75,00€',
	},
]

const defaultTotals: DriverServiceTotalsByTypeRow[] = [
	{
		id: '1',
		tipo: 'Agências Viagens',
		numero: '5',
		valor: '405,90',
		kmsCarga: '750',
		kmsVazio: '50',
		totalKms: '800',
	},
	{
		id: '2',
		tipo: 'Ocasional',
		numero: '10',
		valor: '525,00',
		kmsCarga: '1140',
		kmsVazio: '196',
		totalKms: '1336',
	},
	{
		id: '3',
		tipo: 'Totais',
		numero: '15',
		valor: '930,90',
		kmsCarga: '1890',
		kmsVazio: '246',
		totalKms: '2136',
	},
]

const defaultStats = {
	kmsCarga: '1890',
	kmsVazio: '246',
	totalValue: '930,90',
}

const defaultTachoRows = [
	{
		id: '1',
		numMec: '12345',
		data: '15/03/2024',
		dataReal: '15/03/2024',
		nomeFicheiro: 'Lorem Ipsum',
		nome: 'Lorem Ipsum',
	},
	{
		id: '2',
		numMec: '12345',
		data: '16/03/2024',
		dataReal: '16/03/2024',
		nomeFicheiro: 'Lorem Ipsum',
		nome: 'Lorem Ipsum',
	},
]

const CustomModalDriverServiceTotals: React.FC = () => {
	const { isOpen, closeModal, modalData, modalHandlers, openModal } = useModal()
	const navigate = useNavigate()
	const data = modalData as DriverServiceTotalsModalData | undefined
	const [handlePrint, handleSend, handleExport] = modalHandlers ?? []

	const [startDate, setStartDate] = useState<Dayjs | null>(
		data?.startDate ? dayjs(data.startDate, 'DD/MM/YYYY') : dayjs('02/05/2024', 'DD/MM/YYYY'),
	)
	const [endDate, setEndDate] = useState<Dayjs | null>(
		data?.endDate ? dayjs(data.endDate, 'DD/MM/YYYY') : dayjs('08/05/2024', 'DD/MM/YYYY'),
	)
	const [filial, setFilial] = useState(data?.filial ?? 'Transol')
	const [driver, setDriver] = useState(data?.driver ?? 'Alexandre Gomes')
	const [tachoStartDate, setTachoStartDate] = useState<Dayjs | null>(dayjs('01/01/2024', 'DD/MM/YYYY'))
	const [tachoEndDate, setTachoEndDate] = useState<Dayjs | null>(dayjs('31/12/2024', 'DD/MM/YYYY'))
	const [tachoDriver, setTachoDriver] = useState(data?.driver ?? 'Alexandre Gomes')
	const [tachoOnlyActive, setTachoOnlyActive] = useState(true)
	const [includeScheduleTime, setIncludeScheduleTime] = useState(data?.includeScheduleTime ?? true)
	const [servicesDensity, setServicesDensity] = useState<DensityMode>('standard')
	const [totalsDensity, setTotalsDensity] = useState<DensityMode>('compact')
	const [tachoDialogOpen, setTachoDialogOpen] = useState(false)
	const [tachoDensity, setTachoDensity] = useState<DensityMode>('compact')
	const [rowMenuAnchorEl, setRowMenuAnchorEl] = useState<null | HTMLElement>(null)
	const [rowMenuId, setRowMenuId] = useState<string | number | null>(null)
	const [animateRows, setAnimateRows] = useState(false)
	const rowAnimationTimeoutRef = useRef<number | null>(null)

	const services = data?.services?.length ? data.services : defaultServices
	const totals = data?.totals?.length ? data.totals : defaultTotals
	const stats = { ...defaultStats, ...(data?.stats ?? {}) }
	const driverCost = data?.driverCost ?? '0,00'
	const footerVariant = data?.footerVariant ?? 'export'
	const tachoRows = defaultTachoRows

	useEffect(() => {
		if (!isOpen) {
			setAnimateRows(false)
			return
		}

		setAnimateRows(true)
		if (rowAnimationTimeoutRef.current !== null) {
			window.clearTimeout(rowAnimationTimeoutRef.current)
		}
		rowAnimationTimeoutRef.current = window.setTimeout(() => {
			setAnimateRows(false)
		}, 260)

		return () => {
			if (rowAnimationTimeoutRef.current !== null) {
				window.clearTimeout(rowAnimationTimeoutRef.current)
			}
		}
	}, [isOpen])

	const handleOpenRowMenu = (event: React.MouseEvent<HTMLButtonElement>, rowId: string | number) => {
		setRowMenuAnchorEl(event.currentTarget)
		setRowMenuId(rowId)
	}

	const handleCloseRowMenu = () => {
		setRowMenuAnchorEl(null)
		setRowMenuId(null)
	}

	const handleRowMenuAction = (action: string) => {
		const selectedRow = services.find(row => row.id === rowMenuId)
		if (action === 'Ficha da Viatura') {
			const vehicleId = selectedRow?.viatura ?? rowMenuId ?? 1
			handleCloseRowMenu()
			closeModal()
			navigate(`/schedules/vehicle/${vehicleId}`)
			return
		}

		if (action === 'Serviços e Totais da Viatura') {
			const modalData: VehicleServiceTotalsModalData = {
				vehicleId: selectedRow?.viatura ?? 1,
				startDate: '02/05/2024',
				endDate: '08/05/2024',
				filial: 'Transol',
				vehicle: selectedRow?.viatura ?? '0046',
				includeScheduleTime: true,
				services: [],
				totals: [],
				stats: {
					kmsCarga: '1890',
					kmsVazio: '246',
					totalKms: '2136',
					duracao: '22:30',
				},
			}
			handleCloseRowMenu()
			openModal('Servicos e Totais por Viatura', modalData, [])
			return
		}

		console.log('Serviços por motorista menu:', action, rowMenuId)
		handleCloseRowMenu()
	}

	const serviceColumns = useMemo<GridColDef<DriverServiceTotalsRow>[]>(
		() => [
			{ field: 'ponto', headerName: 'Ponto', width: 68 },
			{ field: 'requisicao', headerName: 'Requisição', width: 101 },
			{ field: 'viatura', headerName: 'Viatura', width: 79 },
			{ field: 'dataInicio', headerName: 'Data Início', width: 100 },
			{ field: 'horaInicio', headerName: 'Hora Início', width: 99 },
			{ field: 'localInicio', headerName: 'Local Início', width: 120 },
			{ field: 'localTermino', headerName: 'Local Término', width: 120 },
			{ field: 'horaTermino', headerName: 'Hora Término', width: 117 },
			{ field: 'dataTermino', headerName: 'Data Término', width: 117 },
			{ field: 'cliente', headerName: 'Cliente', width: 138 },
			{ field: 'valor', headerName: 'Valor', width: 90 },
			{
				field: 'actions',
				headerName: '',
				width: 40,
				renderCell: params => (
					<Box
						className='serviceRowActions'
						sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}
					>
						<IconButton
							size='small'
							onClick={event => handleOpenRowMenu(event, params.row.id)}
							sx={{ padding: 0.5 }}
						>
							<MoreVertRoundedIcon sx={{ fontSize: '18px', color: '#00A5FF' }} />
						</IconButton>
					</Box>
				),
			},
		],
		[handleOpenRowMenu],
	)

	const totalsColumns = useMemo<GridColDef<DriverServiceTotalsByTypeRow>[]>(
		() => [
			{ field: 'tipo', headerName: 'Tipo', width: 140 },
			{ field: 'numero', headerName: 'Número Serviços', width: 138 },
			{ field: 'valor', headerName: 'Valor (€)', width: 100 },
			{ field: 'kmsCarga', headerName: 'Kms Carga', width: 100 },
			{ field: 'kmsVazio', headerName: 'Kms Vazio', width: 100 },
			{ field: 'totalKms', headerName: 'Total Kms', width: 100 },
		],
		[],
	)

	const tachoColumns = useMemo<GridColDef[]>(
		() => [
			{ field: 'numMec', headerName: 'Núm Mec', width: 110 },
			{ field: 'data', headerName: 'Data', width: 110 },
			{ field: 'dataReal', headerName: 'Data Real', width: 120 },
			{ field: 'nomeFicheiro', headerName: 'Nome Ficheiro', minWidth: 160, flex: 1 },
			{ field: 'nome', headerName: 'Nome', minWidth: 160, flex: 1 },
		],
		[],
	)

	const handleClearFilters = () => {
		setStartDate(dayjs('02/05/2024', 'DD/MM/YYYY'))
		setEndDate(dayjs('08/05/2024', 'DD/MM/YYYY'))
		setFilial('Transol')
		setDriver('Alexandre Gomes')
		setIncludeScheduleTime(true)
	}

	const handleOpenDriverSheet = () => {
		const driverId = data?.driverId ?? 1
		closeModal()
		navigate(`/schedules/driver/${driverId}`)
	}

	const handleOpenTachograph = () => {
		setTachoDialogOpen(true)
	}

	const handleCloseTachograph = () => {
		setTachoDialogOpen(false)
	}

	const handleOpenDriverFromTacho = () => {
		setTachoDialogOpen(false)
		handleOpenDriverSheet()
	}

	const handlePrintTotals = () => {
		if (typeof handlePrint === 'function') {
			handlePrint(services)
			return
		}
		console.log('Imprimir serviços por motorista', services)
	}

	const handleSendTotals = () => {
		if (typeof handleSend === 'function') {
			handleSend(services)
			return
		}
		console.log('Enviar serviços por motorista', services)
	}

	const handleExportTotals = () => {
		if (typeof handleExport === 'function') {
			handleExport(services)
			return
		}
		console.log('Exportar serviços por motorista', services)
	}

	return (
		<>
			<Dialog
				open={isOpen}
				onClose={closeModal}
				maxWidth={false}
				sx={{ pointerEvents: 'auto', zIndex: 2000 }}
				PaperProps={{
					sx: {
						width: 'min(1200px, 96vw)',
						borderRadius: '6px',
						backgroundColor: '#FFFFFF',
						boxShadow: dialogShadow,
						overflow: 'hidden',
						zIndex: 2001,
					},
				}}
			>
				<Box sx={{ fontFamily: 'Poppins, sans-serif', color: '#092056' }}>
					<Box sx={{ padding: '16px 24px' }}>
						<Typography
							sx={{
								fontSize: '16px',
								fontWeight: 600,
								lineHeight: 1.75,
								letterSpacing: '0.15px',
								color: '#092056',
							}}
						>
							Serviços e Totais por Motorista
						</Typography>
					</Box>

					<Box sx={{ padding: '0 24px 24px', display: 'grid', gap: '24px' }}>
						<Box
							sx={{
								display: 'flex',
								flexWrap: 'wrap',
								alignItems: 'center',
								gap: '16px',
							}}
						>
							<LocalizationProvider dateAdapter={AdapterDayjs}>
								<DatePicker
									label='Data Inicial'
									value={startDate}
									onChange={newValue => setStartDate(newValue)}
									format='DD/MM/YYYY'
									slotProps={{
										textField: {
											size: 'small',
											sx: [inputSx, calendarInputSx, { maxWidth: '140px' }],
										},
										...calendarPopperSlotProps,
									}}
									slots={{ openPickerButton: EmptyButton }}
								/>
							</LocalizationProvider>
							<Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#092056' }}>
								{String.fromCharCode(0x2014)}
							</Typography>
							<LocalizationProvider dateAdapter={AdapterDayjs}>
								<DatePicker
									label='Data Final'
									value={endDate}
									onChange={newValue => setEndDate(newValue)}
									format='DD/MM/YYYY'
									slotProps={{
										textField: {
											size: 'small',
											sx: [inputSx, calendarInputSx, { maxWidth: '140px' }],
										},
										...calendarPopperSlotProps,
									}}
									slots={{ openPickerButton: EmptyButton }}
								/>
							</LocalizationProvider>
							<TextField
								select
								label='Filial'
								value={filial}
								onChange={event => setFilial(event.target.value)}
								size='small'
								sx={{ ...inputSx, minWidth: '160px' }}
								SelectProps={{ IconComponent: KeyboardArrowDownIcon }}
							>
								<MenuItem value='Transol'>Transol</MenuItem>
							</TextField>
							<TextField
								select
								label='Motorista'
								value={driver}
								onChange={event => setDriver(event.target.value)}
								size='small'
								sx={{ ...inputSx, minWidth: '160px' }}
								SelectProps={{ IconComponent: KeyboardArrowDownIcon }}
							>
								<MenuItem value='Alexandre Gomes'>Alexandre Gomes</MenuItem>
								<MenuItem value='Beatriz Silva'>Beatriz Silva</MenuItem>
							</TextField>
							<FormControlLabel
								control={
									<Checkbox
										size='small'
										checked={includeScheduleTime}
										onChange={event => setIncludeScheduleTime(event.target.checked)}
										sx={checkboxSx}
									/>
								}
								label={<Typography sx={checkboxLabelSx}>Hora da Escala</Typography>}
								sx={{ margin: 0 }}
							/>
							<CustomButton
								variant='text'
								size='small'
								onClick={handleClearFilters}
								sx={{
									color: '#00A5FF',
									fontWeight: 600,
									fontSize: '12px',
									textTransform: 'none',
									marginLeft: 'auto',
									padding: '4px 0',
									'&:hover': { backgroundColor: 'transparent' },
								}}
							>
								Limpar Filtros
							</CustomButton>
						</Box>

						<Box
							sx={{
								display: 'flex',
								flexWrap: 'wrap',
								alignItems: 'center',
								justifyContent: 'space-between',
								gap: '16px',
							}}
						>
							<Typography
								sx={{
									fontSize: '24px',
									fontWeight: 600,
									lineHeight: 1.334,
									color: '#092056',
								}}
							>
								{services.length} Serviços
							</Typography>
							<Box sx={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
								{[
									{ label: 'Kms Carga', value: stats.kmsCarga, minWidth: '93px' },
									{ label: 'Kms Vazio', value: stats.kmsVazio, minWidth: '93px' },
									{ label: 'Total Serviços(€)', value: stats.totalValue, minWidth: '133px' },
								].map(stat => (
									<TextField
										key={stat.label}
										label={stat.label}
										value={stat.value}
										size='small'
										InputProps={{ readOnly: true }}
										InputLabelProps={{ shrink: true }}
										sx={{ ...readonlyFieldSx, width: stat.minWidth }}
									/>
								))}
								<CustomButton
									size='small'
									onClick={handleOpenDriverSheet}
									sx={{
										background: '#00A5FF',
										color: '#FFFFFF',
										textTransform: 'none',
										borderRadius: '4px',
										padding: '4px 10px',
										fontWeight: 600,
										fontSize: '14px',
										lineHeight: '22px',
									}}
								>
									Ficha Motorista
								</CustomButton>
								<CustomButton
									size='small'
									onClick={handleOpenTachograph}
									sx={{
										background: '#00A5FF',
										color: '#FFFFFF',
										textTransform: 'none',
										borderRadius: '4px',
										padding: '4px 10px',
										fontWeight: 600,
										fontSize: '14px',
										lineHeight: '22px',
									}}
								>
									Tacógrafo
								</CustomButton>
								<ToggleButtonGroup
									value={servicesDensity}
									exclusive
									onChange={(_, value) => value && setServicesDensity(value)}
									size='small'
									sx={toggleGroupSx}
								>
									<ToggleButton
										value='compact'
										aria-label='densidade compacta'
									>
										<TableRowsIcon
											sx={{
												width: '14.4px',
												height: '14.4px',
												color: servicesDensity === 'compact' ? '#00A5FF' : 'rgba(134, 149, 175, 1)',
											}}
										/>
									</ToggleButton>
									<ToggleButton
										value='standard'
										aria-label='densidade espaçada'
									>
										<ViewHeadlineIcon
											sx={{
												width: '14.4px',
												height: '14.4px',
												color: servicesDensity === 'standard' ? '#00A5FF' : 'rgba(134, 149, 175, 1)',
											}}
										/>
									</ToggleButton>
								</ToggleButtonGroup>
							</Box>
						</Box>

						<AnimatedDataGridPro
							slots={{ columnMenu: CustomColumnMenu }}
							slotProps={{ basePopper: { sx: columnMenuPopperSx } }}
							rows={services}
							columns={serviceColumns}
							density={servicesDensity}
							rowHeight={servicesDensity === 'compact' ? 32 : 40}
							columnHeaderHeight={40}
							autoHeight
							disableRowSelectionOnClick
							hideFooter
							getRowClassName={params => {
								const classes = []
								if (animateRows) classes.push('row--animate')
								if (params.indexRelativeToCurrentPage === 2) classes.push('row--warning')
								if (params.indexRelativeToCurrentPage === 3) classes.push('row--alert')
								return classes.join(' ')
							}}
							sx={[tableSx, servicesTableSx, getDynamicRowStyles(servicesDensity)]}
						/>
						<Menu
							anchorEl={rowMenuAnchorEl}
							open={Boolean(rowMenuAnchorEl)}
							onClose={handleCloseRowMenu}
							PaperProps={{ sx: menuPaperSx }}
							MenuListProps={{ sx: { padding: '8px 0' } }}
						>
							<MenuItem
								onClick={() => handleRowMenuAction('Ficha da Viatura')}
								sx={{
									fontFamily: 'Poppins, sans-serif',
									fontSize: '12px',
									fontWeight: 500,
									lineHeight: 1.5,
									color: '#092056',
									padding: '6px 16px',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'space-between',
									gap: '16px',
								}}
							>
								<span>Ficha da Viatura</span>
								<ChevronRightRoundedIcon sx={{ fontSize: '18px', color: '#00A5FF' }} />
							</MenuItem>
							<MenuItem
								onClick={() => handleRowMenuAction('Serviços e Totais da Viatura')}
								sx={{
									fontFamily: 'Poppins, sans-serif',
									fontSize: '12px',
									fontWeight: 500,
									lineHeight: 1.5,
									color: '#092056',
									padding: '6px 16px',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'space-between',
									gap: '16px',
								}}
							>
								<span>Serviços e Totais da Viatura</span>
								<ChevronRightRoundedIcon sx={{ fontSize: '18px', color: '#00A5FF' }} />
							</MenuItem>
						</Menu>

						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'space-between',
								flexWrap: 'wrap',
								gap: '12px',
							}}
						>
							<Typography
								sx={{
									fontSize: '24px',
									fontWeight: 600,
									lineHeight: 1.334,
									color: '#092056',
								}}
							>
								Totais por Tipo de Serviço
							</Typography>
							<Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
								<TextField
									label='Custos do Motorista (€)'
									value={driverCost}
									size='small'
									InputProps={{ readOnly: true }}
									InputLabelProps={{ shrink: true }}
									sx={{ ...readonlyFieldSx, width: '163px' }}
								/>
								<Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
									<EditRoundedIcon sx={{ color: '#00A5FF', fontSize: '16px' }} />
									<ToggleButtonGroup
										value={totalsDensity}
										exclusive
										onChange={(_, value) => value && setTotalsDensity(value)}
										size='small'
										sx={toggleGroupSx}
									>
										<ToggleButton
											value='compact'
											aria-label='densidade compacta'
										>
											<TableRowsIcon
												sx={{
													width: '14.4px',
													height: '14.4px',
													color: totalsDensity === 'compact' ? '#00A5FF' : 'rgba(134, 149, 175, 1)',
												}}
											/>
										</ToggleButton>
										<ToggleButton
											value='standard'
											aria-label='densidade espaçada'
										>
											<ViewHeadlineIcon
												sx={{
													width: '14.4px',
													height: '14.4px',
													color: totalsDensity === 'standard' ? '#00A5FF' : 'rgba(134, 149, 175, 1)',
												}}
											/>
										</ToggleButton>
									</ToggleButtonGroup>
								</Box>
							</Box>
						</Box>

						<AnimatedDataGridPro
							slots={{ columnMenu: CustomColumnMenu }}
							slotProps={{ basePopper: { sx: columnMenuPopperSx } }}
							rows={totals}
							columns={totalsColumns}
							density={totalsDensity}
							rowHeight={totalsDensity === 'compact' ? 32 : 40}
							columnHeaderHeight={40}
							autoHeight
							disableRowSelectionOnClick
							hideFooter
							getRowClassName={params => {
								const classes = []
								if (animateRows) classes.push('row--animate')
								if (params.row?.tipo === 'Totais') classes.push('row--totals')
								return classes.join(' ')
							}}
							sx={[tableSx, totalsTableSx, getDynamicRowStyles(totalsDensity)]}
						/>
					</Box>

					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'flex-end',
							gap: '8px',
							padding: '8px 24px 16px',
						}}
					>
						<CustomButton
							variant='text'
							onClick={closeModal}
							sx={popupSecondaryButtonSx}
						>
							Fechar
						</CustomButton>
						{footerVariant === 'send' ? (
							<>
								<CustomButton
									variant='text'
									onClick={handlePrintTotals}
									sx={popupSecondaryButtonSx}
								>
									Imprimir
								</CustomButton>
								<CustomButton
									variant='contained'
									color='primary'
									onClick={handleSendTotals}
									sx={popupActionButtonSx}
								>
									Enviar
								</CustomButton>
							</>
						) : (
							<>
								<CustomButton
									variant='text'
									onClick={handleExportTotals}
									sx={popupSecondaryButtonSx}
								>
									Exportar
								</CustomButton>
								<CustomButton
									variant='contained'
									color='primary'
									onClick={handlePrintTotals}
									sx={popupActionButtonSx}
								>
									Imprimir
								</CustomButton>
							</>
						)}
					</Box>
				</Box>
			</Dialog>
			<Dialog
				open={tachoDialogOpen}
				onClose={handleCloseTachograph}
				maxWidth={false}
				sx={{ pointerEvents: 'auto', zIndex: 2000 }}
				PaperProps={{
					sx: {
						width: 'min(1100px, 96vw)',
						borderRadius: '4px',
						backgroundColor: '#FFFFFF',
						boxShadow: dialogShadow,
						overflow: 'hidden',
						zIndex: 2001,
					},
				}}
			>
				<Box sx={{ fontFamily: 'Poppins, sans-serif', color: '#092056' }}>
					<Box sx={{ padding: '16px 24px 0' }}>
						<Typography
							sx={{
								fontSize: '16px',
								fontWeight: 600,
								lineHeight: 1.75,
								letterSpacing: '0.15px',
								color: '#092056',
							}}
						>
							Digitalização dos Discos de Tacógrafo
						</Typography>
					</Box>
					<Box sx={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '24px' }}>
						<Box
							sx={{
								display: 'flex',
								flexWrap: 'wrap',
								alignItems: 'center',
								gap: '16px',
								justifyContent: 'space-between',
							}}
						>
							<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
								<LocalizationProvider dateAdapter={AdapterDayjs}>
									<DatePicker
										label='Data Inicial'
										value={tachoStartDate}
										onChange={newValue => setTachoStartDate(newValue)}
										format='DD/MM/YYYY'
										slotProps={{
											textField: {
												size: 'small',
												sx: [inputSx, calendarInputSx, { minWidth: '140px' }],
											},
											...calendarPopperSlotProps,
										}}
										slots={{ openPickerButton: EmptyButton }}
									/>
								</LocalizationProvider>
								<Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#092056' }}>
									{String.fromCharCode(0x2014)}
								</Typography>
								<LocalizationProvider dateAdapter={AdapterDayjs}>
									<DatePicker
										label='Data Final'
										value={tachoEndDate}
										onChange={newValue => setTachoEndDate(newValue)}
										format='DD/MM/YYYY'
										slotProps={{
											textField: {
												size: 'small',
												sx: [inputSx, calendarInputSx, { minWidth: '140px' }],
											},
											...calendarPopperSlotProps,
										}}
										slots={{ openPickerButton: EmptyButton }}
									/>
								</LocalizationProvider>
								<TextField
									select
									label='Motorista'
									value={tachoDriver}
									onChange={event => setTachoDriver(event.target.value)}
									size='small'
									sx={{ ...inputSx, minWidth: '180px' }}
									SelectProps={{ IconComponent: KeyboardArrowDownIcon }}
								>
									<MenuItem value={tachoDriver}>{tachoDriver}</MenuItem>
								</TextField>
								<FormControlLabel
									control={
										<Checkbox
											size='small'
											checked={tachoOnlyActive}
											onChange={event => setTachoOnlyActive(event.target.checked)}
											sx={checkboxSx}
										/>
									}
									label={<Typography sx={checkboxLabelSx}>Apenas Ativos</Typography>}
								/>
							</Box>
							<CustomButton
								variant='text'
								size='small'
								sx={{
									color: '#00A5FF',
									fontWeight: 600,
									fontSize: '12px',
									textTransform: 'none',
									padding: '4px 0',
								}}
							>
								Limpar Filtros
							</CustomButton>
						</Box>

						<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
							<Typography
								sx={{
									fontSize: '24px',
									fontWeight: 600,
									lineHeight: 1.334,
									color: '#092056',
								}}
							>
								{tachoRows.length} Registos
							</Typography>
							<Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
								<CustomButton
									size='small'
									onClick={handleOpenDriverFromTacho}
									sx={{
										background: '#00A5FF',
										color: '#FFFFFF',
										textTransform: 'none',
										borderRadius: '4px',
										padding: '6px 12px',
										fontWeight: 600,
										fontSize: '12px',
										lineHeight: '20px',
									}}
								>
									Ficha Motorista
								</CustomButton>
								<ToggleButtonGroup
									value={tachoDensity}
									exclusive
									onChange={(_, value) => value && setTachoDensity(value)}
									size='small'
									sx={toggleGroupSx}
								>
									<ToggleButton
										value='compact'
										aria-label='densidade compacta'
									>
										<TableRowsIcon
											sx={{
												width: '14.4px',
												height: '14.4px',
												color: tachoDensity === 'compact' ? '#00A5FF' : 'rgba(134, 149, 175, 1)',
											}}
										/>
									</ToggleButton>
									<ToggleButton
										value='standard'
										aria-label='densidade espaçada'
									>
										<ViewHeadlineIcon
											sx={{
												width: '14.4px',
												height: '14.4px',
												color: tachoDensity === 'standard' ? '#00A5FF' : 'rgba(134, 149, 175, 1)',
											}}
										/>
									</ToggleButton>
								</ToggleButtonGroup>
							</Box>
						</Box>

						<AnimatedDataGridPro
							slots={{ columnMenu: CustomColumnMenu }}
							slotProps={{ basePopper: { sx: columnMenuPopperSx } }}
							rows={tachoRows}
							columns={tachoColumns}
							density={tachoDensity}
							rowHeight={tachoDensity === 'compact' ? 32 : 40}
							columnHeaderHeight={40}
							autoHeight
							disableRowSelectionOnClick
							hideFooter
							getRowClassName={params => {
								const classes = []
								if (animateRows) classes.push('row--animate')
								return classes.join(' ')
							}}
							sx={[tableSx, getDynamicRowStyles(tachoDensity)]}
						/>
					</Box>

					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'flex-end',
							gap: '12px',
							padding: '8px 16px 16px',
						}}
					>
						<CustomButton
							variant='contained'
							onClick={handleCloseTachograph}
							sx={popupSecondaryButtonSx}
						>
							Fechar
						</CustomButton>
						<CustomButton
							variant='text'
							size='small'
							sx={popupSecondaryButtonSx}
						>
							Imprimir
						</CustomButton>
						<CustomButton
							variant='contained'
							size='small'
							sx={popupActionButtonSx}
						>
							Enviar
						</CustomButton>
					</Box>
				</Box>
			</Dialog>
		</>
	)
}

export default CustomModalDriverServiceTotals
